'use client';

import { useRef, useState, useEffect } from 'react';

// Singleton: track if YT API script is loading/loaded
let apiLoadPromise: Promise<void> | null = null;

function loadYouTubeAPI(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  // Runtime-real check, type-level false positive: the iframe API script loads
  // async, so window.YT IS undefined until then — but the ambient `namespace
  // YT` declaration (types/youtube.d.ts) binds a global VALUE that TypeScript
  // cannot model as optional without giving up the namespace types.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (window.YT?.Player) return Promise.resolve();
  if (apiLoadPromise) return apiLoadPromise;

  apiLoadPromise = new Promise<void>((resolve, reject) => {
    const existingCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      existingCallback?.();
      resolve();
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.onerror = () => { apiLoadPromise = null; reject(new Error('YouTube API failed to load')); };
    document.head.appendChild(script);
  });

  return apiLoadPromise;
}


interface UseYouTubePlayerOptions {
  containerId: string;
  videoId: string;
  enabled: boolean;
  autoplay?: boolean;
  onReady?: () => void;
  onStateChange?: (state: number) => void;
  onEnd?: () => void;
}

export function useYouTubePlayer({
  containerId,
  videoId,
  enabled,
  autoplay = true,
  onReady,
  onStateChange,
  onEnd,
}: UseYouTubePlayerOptions) {
  const playerRef = useRef<YT.Player | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Stabilize callbacks via refs to prevent player recreation on re-render
  const onReadyRef = useRef(onReady);
  const onStateChangeRef = useRef(onStateChange);
  const onEndRef = useRef(onEnd);
  useEffect(() => { onReadyRef.current = onReady; onStateChangeRef.current = onStateChange; onEndRef.current = onEnd; });

  useEffect(() => {
    if (!enabled) return;

    let destroyed = false;

    loadYouTubeAPI().then(() => {
      if (destroyed) return;

      playerRef.current = new window.YT.Player(containerId, {
        videoId,
        playerVars: {
          autoplay: autoplay ? 1 : 0,
          rel: 0,
          controls: 0,
          modestbranding: 1,
          iv_load_policy: 3,
          cc_load_policy: 0,
          playsinline: 1,
        },
        events: {
          onReady: () => {
            if (destroyed) return;
            setIsReady(true);
            if (autoplay) playerRef.current?.playVideo();
            onReadyRef.current?.();
          },
          onStateChange: (event: YT.OnStateChangeEvent) => {
            if (destroyed) return;
            onStateChangeRef.current?.(event.data);
            if (event.data === YT.PlayerState.ENDED) {
              onEndRef.current?.();
            }
          },
        },
      });
    }).catch((error: unknown) => {
      // Loader failed (network / blocked script) — surface it, else the player
      // silently never initializes and there's nothing to diagnose.
      console.error('YouTube IFrame API failed to load:', error);
    });

    return () => {
      destroyed = true;
      playerRef.current?.destroy();
      playerRef.current = null;
      setIsReady(false);
    };
   
  }, [enabled, autoplay, containerId, videoId]);

  return { player: playerRef, isReady };
}
